import { toast } from '@ordo/ui/sonner'
import { useEffect, useMemo } from 'react'
import type { DashboardData, DeliveryStats, DeliveryStatsResponse } from 'types/dashboard'
import type { QueryResult } from 'utils/api'
import { aggregateDeliveryStats, mergeDeliveryDays } from 'utils/delivery-stats'

import { useApiQuery } from '../useApiQuery'
import { getApiErrorMessage } from '../useFetchApi'

export function useDashboard(): QueryResult<DashboardData> {
  const result = useApiQuery<DashboardData>('/api/dashboard')

  // Without this a failed load told the user nothing: `dashboard.tsx`
  // renders on `loading || !data`, so a 500 left the skeleton up forever
  // (PROD-992). TanStack catches the rejection; we surface it here.
  const { error } = result
  useEffect(() => {
    if (error) {
      toast.error('Failed to load dashboard', { description: getApiErrorMessage(error) })
    }
  }, [error])

  return result
}

const DELIVERY_STATS_PERIOD_DAYS = 30
/**
 * Days older than this are treated as settled: SendGrid daily stats are an
 * append-mostly time series, so history is cached aggressively while the
 * recent window is always fetched fresh. Opens/clicks that trickle into
 * older days surface on the next session (or after the 24h cache expiry) —
 * widen the horizon if that ever matters.
 */
const SETTLE_HORIZON_DAYS = 2
const DAY_MS = 24 * 60 * 60 * 1000
const HISTORY_GC_TIME_MS = 24 * 60 * 60 * 1000

function dayString(daysAgo: number): string {
  return new Date(Date.now() - daysAgo * DAY_MS).toISOString().slice(0, 10)
}

export interface DeliveryStatsResult {
  /** null while loading OR when SendGrid is unavailable. */
  data: DeliveryStats | null
  loading: boolean
}

/**
 * SendGrid delivery stats for the last 30 days, split into two windows:
 *
 * - **History** (30d ago → settle horizon): fetched once and cached for the
 *   session (`staleTime: Infinity`) — these days no longer change.
 * - **Live** (settle horizon → today): always fetched fresh — on mount, on
 *   window focus, and every 60s. The numbers that actually move are never
 *   served without a fresh fetch in flight.
 *
 * The windows share their boundary day; the merge dedupes by date with the
 * live slice winning.
 */
export function useDeliveryStats(): DeliveryStatsResult {
  const today = dayString(0)
  const horizon = dayString(SETTLE_HORIZON_DAYS)
  const startDate = dayString(DELIVERY_STATS_PERIOD_DAYS)

  const history = useApiQuery<DeliveryStatsResponse>(
    `/api/dashboard/delivery-stats?startDate=${startDate}&endDate=${horizon}`,
    { staleTime: Number.POSITIVE_INFINITY, gcTime: HISTORY_GC_TIME_MS },
  )
  const live = useApiQuery<DeliveryStatsResponse>(
    `/api/dashboard/delivery-stats?startDate=${horizon}&endDate=${today}`,
    { refetchOnWindowFocus: true, refetchInterval: 60_000 },
  )

  const historyDays = history.data?.days
  const liveDays = live.data?.days
  const data = useMemo(() => {
    if (historyDays === undefined || liveDays === undefined || historyDays === null || liveDays === null) {
      return null
    }
    return aggregateDeliveryStats(mergeDeliveryDays(historyDays, liveDays), {
      periodDays: DELIVERY_STATS_PERIOD_DAYS,
      startDate,
    })
  }, [historyDays, liveDays, startDate])

  return { data, loading: history.loading || live.loading }
}
